import {
  CSSProperties,
  ReactPortal,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import {
  Arrow,
  DisappearType,
  IBounds,
  TriggerProps,
  useLayer,
  type Placement,
} from "react-laag";
import "../../styles/popup/tooltip.css";
import { ExpandingTooltip } from "../tooltips/ExpandingTooltip";

export { useAdvancedTooltip, ExpandingTooltipReturn };

type ExpandingTooltipReturn = {
  /* If tooltip visible */
  isOpen: boolean;
  /* Set tooltip visibility */
  setOpen: (open: boolean) => void;
  /* If tooltip is minimized */
  isShort: boolean;
  /* Set tooltip to be expanded/minimized */
  setShort: (long: boolean) => void;
  /* Render tooltip method */
  renderTooltip: () => ReactPortal | null;
  /* TODO: describe */
  triggerProps: TriggerProps & {
    onMouseEnter?: VoidFunction;
    onMouseLeave?: VoidFunction;
  };
  /* TODO: describe */
  triggerBounds: DOMRect | IBounds | null;
  /** MouseOver event listener to open tooltip */
  // triggerMouseOver: VoidFunction;
  /** MouseOut event listener to close tooltip if mouse not approaching tooltip. */
  // triggerMouseOut: React.MouseEventHandler<HTMLElement>;
};

type Props = {
  noLongText?: boolean;
  tooltipText?: ExpandingTooltip;
  /** If true, the tooltip is always allowed to overflow the container */
  overflow?: boolean;
  // shortText: () => JSX.Element | undefined;
  // longText: () => JSX.Element | undefined;
  containerRef: React.MutableRefObject<HTMLElement | null | undefined>;
  /* default: Close tooltip. */
  onOutsideClick?: VoidFunction;
  /* default: Close tooltip when fully disappeared. */
  onDisappear?: (disappearType: DisappearType) => void;
  /* default: Close tooltip. */
  onParentClose?: VoidFunction;
  /* default: 'top-center'. */
  placement?: Placement;
  /* default: ['top-center', 'bottom-center']. */
  possiblePlacements?: Placement[];
  /* style for .tooltip element. */
  tooltipLayerStyle?: CSSProperties;
};
const ARROW_OFFSET = 9;
const DEFAULT_PLACEMENT_TYPES: Placement[] = [
  "bottom-start",
  "bottom-end",
  "bottom-center",
  "top-start",
  "top-center",
  "top-end",
  "left-end",
  "left-center",
  "left-start",
  "right-end",
  "right-center",
  "right-start",
  "center",
];

const TOOLTIP_IN_TIMEOUT_MS = Number(
  import.meta.env.VITE_TOOLTIP_IN_TIMEOUT_MS,
);

const TOOLTIP_LEAVE_TIMEOUT_MS = Number(
  import.meta.env.VITE_TOOLTIP_LEAVE_TIMEOUT_MS,
);

const useAdvancedTooltip = (_props: Props): ExpandingTooltipReturn => {
  const defaults: Partial<Props> = {
    placement: "top-center",
    possiblePlacements: DEFAULT_PLACEMENT_TYPES,
  };

  const idRef = useRef(Math.random());

  const props = { ...defaults, ..._props };

  const hoverInTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const hoverOutTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const [isOpen, setOpen] = useState<boolean>(false);
  const [isShort, setShort] = useState<boolean>(true);

  const [intersectTrigger, setIntersectTrigger] = useState(false);
  const [intersectLayer, setIntersectLayer] = useState(false);

  const {
    renderLayer,
    triggerProps,
    layerProps,
    arrowProps,
    layerSide,
    triggerBounds,
  } = useLayer({
    // default to above, so below inputs are visible. eg, <FuzzyMatcherEditor />, <FuzzyFieldMatcherEditor />
    placement: props.placement,
    possiblePlacements: props.possiblePlacements,
    isOpen,
    overflowContainer: props.overflow
      ? true
      : props.containerRef?.current
        ? true
        : false,
    // placement: 'bottom-end',
    onOutsideClick:
      props.onOutsideClick ??
      (() => {
        setOpen(false);
        setShort(true);
      }),
    auto: true,
    containerOffset: 11,
    arrowOffset: ARROW_OFFSET,
    // attach to button
    // container: props.containerRef?.current ?? undefined,
    onDisappear:
      props.onDisappear ??
      ((disappearType) => {
        if (disappearType == "full") {
          setOpen(false);
          setShort(true);
        }
      }),
    onParentClose:
      props.onParentClose ??
      (() => {
        setOpen(false);
        setShort(true);
      }),
  });

  useEffect(() => {
    // call react-laag triggerRef, otherwise error !
    triggerProps?.ref(props.containerRef.current);
    setOpen(true);
  }, [props.containerRef, props.containerRef?.current]);

  const hoverInTriggerTooltip = () => {
    clearTimeout(hoverInTimeoutRef.current!);
    hoverInTimeoutRef.current = setTimeout(() => {
      // if()
      setIntersectTrigger(true);
    }, TOOLTIP_IN_TIMEOUT_MS);
  };
  const hoverOutTriggerTooltip = () => {
    clearTimeout(hoverOutTimeoutRef.current!);
    hoverOutTimeoutRef.current = setTimeout(() => {
      // if()
      setIntersectTrigger(false);
    }, TOOLTIP_LEAVE_TIMEOUT_MS);
  };

  const handleHoverIn = useCallback(hoverInTriggerTooltip, []);
  const handleHoverOut = useCallback(hoverOutTriggerTooltip, []);

  const renderTooltip = (): ReactPortal | null => {
    return renderLayer(
      <div
        ref={layerProps.ref}
        style={{
          ...layerProps.style,
          ...props.tooltipLayerStyle,
        }}
        className={"tooltip " + layerSide}
        onClick={(e) => e.stopPropagation()}
        onMouseMove={(e) => e.stopPropagation()}
        onMouseEnter={() => setIntersectLayer(true)}
        // onMouseEnter={handleHoverIn}
        onMouseLeave={() => setIntersectLayer(false)}
      >
        {props.tooltipText!(isShort)}
        {isShort && !props.noLongText && (
          <span className={"expand"} onClick={() => setShort(false)}>
            Click to expand
          </span>
        )}
        <Arrow
          className={"arrow " + layerSide}
          {...arrowProps}
          size={5}
          roundness={0}
          id={idRef.current + ""}
        />
      </div>,
    );
  };

  useEffect(() => {
    // console.debug(`[${idRef.current}]: IntscLayer:${intersectLayer},IntscTrigger:${intersectTrigger}`)
    if (!intersectLayer && !intersectTrigger) {
      // console.warn(`[${idRef.current}: LAYER AND TRIGGER NOT FOCUSED]`);
      setOpen(false);
      setShort(true);
    } else {
      setOpen(props.tooltipText != undefined);
    }
  }, [intersectTrigger, intersectLayer]);

  return {
    renderTooltip,
    triggerProps: {
      ...triggerProps,
      onMouseEnter: () => {
        clearTimeout(hoverOutTimeoutRef.current!);
        handleHoverIn();
      },
      onMouseLeave: () => {
        clearTimeout(hoverInTimeoutRef.current!);
        handleHoverOut();
      },
      // onMouseEnter: () => setIntersectTrigger(true),
      // onMouseLeave: () => setIntersectTrigger(false),
      // onMouseEnter: () => undefined,
      // onMouseLeave: () => undefined,
    },
    triggerBounds,
    setOpen,
    isOpen,
    setShort,
    isShort,
  };
};
